import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { trackEvent } from "@/lib/track";

const KEY = "eon_cookie_consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  const choose = (choice) => {
    localStorage.setItem(KEY, choice);
    const state = choice === "accepted" ? "granted" : "denied";
    window.gtag?.("consent", "update", {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
    });
    trackEvent("cookie_consent", { choice });
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-consent-bar"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 inset-x-0 z-[70] border-t border-[#D4AF37]/25 bg-[#0B0C0E]/90 backdrop-blur-xl"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
              <Cookie size={18} className="text-[#D4AF37] shrink-0 mt-0.5 sm:mt-0" />
              <p className="text-xs text-[#9CA3AF] leading-relaxed">
                We use cookies and analytics tags (Google Tag Manager) to measure advertising performance and
                improve your experience. Read our{" "}
                <Link to="/privacy-policy" data-testid="cookie-privacy-link" className="text-[#F3E5AB] underline underline-offset-2 hover:text-[#D4AF37] transition-colors">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                data-testid="cookie-decline-btn"
                onClick={() => choose("declined")}
                className="px-5 py-2.5 rounded-full border border-white/15 font-mono text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] hover:border-white/40 hover:text-[#F3F4F6] transition-all duration-300"
              >
                Decline
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={() => choose("accepted")}
                className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#0B0C0E] font-mono text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-[#F3E5AB] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300"
              >
                Accept
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
